/**
 * Provider seam for signature capture.
 *
 * Every provider — the built-in HTML5 canvas or a hardware pad such as Topaz —
 * ultimately hands the rest of the app the exact same artifact: a transparent
 * PNG `File` named "signature.png". Nothing downstream of `onCapture` (upload,
 * S3 ImageField, Anvil PDF fill, react-pdf footer) knows or cares which device
 * produced it, so adding a device never touches the storage or PDF pipeline.
 *
 * Only *device-backed* providers implement this interface. The canvas is
 * inherently DOM/ref-driven (the user draws directly into a rendered element),
 * so forcing it behind an imperative `capture()` would add indirection without
 * value — it stays as the plain `SignatureDesk` component.
 */

export type SignatureCaptureMethod = "canvas" | "topaz";

export const SIGNATURE_CAPTURE_METHODS: readonly SignatureCaptureMethod[] = [
  "canvas",
  "topaz"
];

export function isSignatureCaptureMethod(
  value: unknown
): value is SignatureCaptureMethod {
  return SIGNATURE_CAPTURE_METHODS.includes(value as SignatureCaptureMethod);
}

/**
 * Why a device is unavailable, coarse enough to pick the remedy to show:
 * install the driver (`service-unreachable`), plug the pad in (`no-device`),
 * or simply try again (`timeout`, `error`).
 */
export type SignatureDeviceFailure =
  | "service-unreachable"
  | "timeout"
  | "no-device"
  | "error";

/** Result of probing for the backing device/service. Never a thrown error. */
export interface SignatureDeviceStatus {
  available: boolean;
  /** Service version string, when the device reports one. */
  version?: string;
  /** Device model string, when the device reports one. Label only. */
  model?: string | null;
  /** Human-readable reason the device is unavailable. Safe to show a user. */
  error?: string;
  /** Machine-readable reason, so the UI can show the matching remedy. */
  failure?: SignatureDeviceFailure;
}

export interface SignatureCaptureOptions {
  /** Target image width in px — mirrors the canvas desk's `width` prop. */
  width: number;
  /** Target image height in px — mirrors the canvas desk's `height` prop. */
  height: number;
}

export interface SignatureCaptureProvider {
  readonly method: SignatureCaptureMethod;

  /** Probe whether the device/service is reachable. Must never throw. */
  isAvailable(): Promise<SignatureDeviceStatus>;

  /** Open a capture session: wipe the pad and arm it for ink. */
  start(options: SignatureCaptureOptions): Promise<void>;

  /**
   * Number of pen points captured so far; 0 until the signer touches the pad.
   * A count rather than a boolean so a poller can tell "new ink since last
   * tick" and refresh the preview only when something changed.
   */
  pointCount(): Promise<number>;

  /**
   * The in-progress ink as an image, or `null` when the device cannot mirror
   * it. Cosmetic — a `null` costs the on-screen preview, never the signature.
   */
  preview(): Promise<Blob | null>;

  /**
   * Read the signature back as a transparent PNG `File`.
   * Throws when the pad is empty — callers surface that to the user.
   */
  capture(): Promise<File>;

  /** Wipe the pad without ending the session. */
  clear(): Promise<void>;

  /** End the session and release the device. Must never throw. */
  stop(): Promise<void>;
}
